import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FaGithub, FaLinkedin, FaEnvelope, FaGlobe,
  FaDownload, FaArrowRight
} from "react-icons/fa";
import Nav from "./Nav";
import "./App.css";

export default function App() {
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem("theme");
    if (saved) return saved;
    return "dark";
  });

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("theme", theme);
  }, [theme]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      const anchor = e.target.closest('a[href^="#"]');
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      const target = href && document.querySelector(href);
      if (target) {
        e.preventDefault();
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, []);

  const projects = [
    {
      id: "exampleproj1",
      title: "Example Project 1",
      desc: "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor.",
      tags: ["Lorem", "Ipsum", "Dolor"],
    },
    {
      id: "exampleproj2",
      title: "Example Project 2",
      desc: "Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi.",
      tags: ["Sit", "Amet", "Consectetur"],
      liveUrl: "#",
      sourceCodeUrl: "#",
    },
    {
      id: "exampleproj3",
      title: "Example Project 3",
      desc: "Duis aute irure dolor in reprehenderit in voluptate velit esse cillum.",
      tags: ["Adipiscing", "Elit", "Sed"],
      liveUrl: "#",
      sourceCodeUrl: "#",
    },
    {
      id: "exampleproj4",
      title: "Example Project 4",
      desc: "Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia.",
      tags: ["Do", "Eiusmod", "Tempor"],
      sourceCodeUrl: "#",
    },
  ];

  const skills = ["React", "JavaScript", "Python", "GoLang", "Linux", "Git", "Node.js", "Automation", "WebRTC", "Security Concepts", "Kotlin", "C#"];

  return (
    <div className="app">
      <Nav theme={theme} setTheme={setTheme} />

      <main>
        {/* HERO — expansive name, intimate content below */}
        <motion.section id="home" className="hero" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <h1 className="hero-name">Nameless<br />Person</h1>
          <p className="hero-tagline">Lorem ipsum dolor sit amet / consectetur adipiscing / elit sed do / eiusmod tempor</p>
          <div className="hero-body">
            <p className="hero-bio">
              Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip.
            </p>
            <div className="hero-actions">
              <a href="#" onClick={(e) => { e.preventDefault(); alert("This would be my resume."); }} className="hero-btn">
                <FaDownload /> R&eacute;sum&eacute;
              </a>
              <a href="#projects" className="hero-btn">
                Projects <FaArrowRight />
              </a>
            </div>
          </div>
          <div className="hero-foot">
            <span>4 projects / 46+ repos</span>
            <div className="hero-socials">
              <a href="#" onClick={(e) => e.preventDefault()}><FaGithub /> GitHub</a>
              <a href="#" onClick={(e) => e.preventDefault()}><FaLinkedin /> LinkedIn</a>
              <a href="#" onClick={(e) => e.preventDefault()}><FaEnvelope /> Email</a>
              <a href="#" onClick={(e) => e.preventDefault()}><FaGlobe /> Web</a>
            </div>
          </div>
        </motion.section>

        {/* PROJECTS — numbered collection */}
        <section id="projects" className="section">
          <h2 className="sec-title">Projects</h2>
          <div className="projects">
            {projects.map((p, i) => (
              <motion.article
                key={p.id}
                className="project"
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ delay: i * 0.06 }}
              >
                <span className="project-num">{String(i + 1).padStart(2, "0")}</span>
                <div className="project-body">
                  <h3>{p.title}</h3>
                  <p>{p.desc}</p>
                </div>
                <div className="project-meta">
                  <div className="project-tags">
                    {p.tags.map(t => <span key={t}>{t}</span>)}
                  </div>
                  <div className="project-links">
                    {p.sourceCodeUrl && (
                      <a href="#" onClick={(e) => e.preventDefault()}>
                        <FaGithub /> Source
                      </a>
                    )}
                    {p.liveUrl && (
                      <a href="#" onClick={(e) => e.preventDefault()}>
                        <FaGlobe /> Live
                      </a>
                    )}
                    <Link to={`/project/${p.id}`} onClick={() => window.scrollTo(0, 0)}>
                      Details <FaArrowRight />
                    </Link>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </section>

        {/* SKILLS — compact text block */}
        <motion.section id="skills" className="section sec-slim" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
          <h2 className="sec-title">Skills</h2>
          <div className="skills-grid">
            {skills.map((skill) => (
              <span key={skill} className="skill-item">{skill}</span>
            ))}
          </div>
        </motion.section>

        {/* ABOUT — two-column editorial */}
        <motion.section id="about" className="section" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
          <h2 className="sec-title">About</h2>
          <div className="about">
            <div className="about-main">
              <p>
                Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation.
              </p>
              <p className="about-note">Lorem ipsum dolor sit amet, consectetur adipiscing.</p>
              <a href="#contact" className="about-cta">
                Say hello <FaArrowRight />
              </a>
            </div>
            <dl className="about-details">
              <div><dt>Location</dt><dd>Lorem ipsum</dd></div>
              <div><dt>Focus</dt><dd>Dolor sit amet</dd></div>
              <div><dt>Languages</dt><dd>Consectetur adipiscing</dd></div>
              <div><dt>Interests</dt><dd>Elit sed do</dd></div>
            </dl>
          </div>
        </motion.section>

        {/* CONTACT — minimal */}
        <motion.section id="contact" className="section" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
          <h2 className="sec-title">Contact</h2>
          <p className="contact-intro">Lorem ipsum dolor sit amet, consectetur adipiscing elit.</p>
          <form
            className="contact-form"
            onSubmit={(e) => {
              e.preventDefault();
              alert("This would be sent to my email.");
            }}
          >
            <div className="f-row">
              <label>
                <span>Name</span>
                <input name="name" type="text" placeholder="Your name" required />
              </label>
              <label>
                <span>Email</span>
                <input name="email" type="email" placeholder="you@example.com" required />
              </label>
            </div>
            <label>
              <span>Subject</span>
              <input name="subject" type="text" placeholder="What's this about?" />
            </label>
            <label>
              <span>Message</span>
              <textarea name="message" rows="4" placeholder="Tell me about your project..." required />
            </label>
            <button type="submit">
              <FaEnvelope /> Send message
            </button>
          </form>
        </motion.section>
      </main>

      <footer className="footer">
        <p>&copy; {new Date().getFullYear()} Nameless</p>
        <a href="#" onClick={(e) => e.preventDefault()}>email@example.com</a>
      </footer>
    </div>
  );
}
